/**
 * Zone Drivers Component
 * Manage drivers assigned to a zone
 */

import { useState } from 'react';
import { Button } from '../ui';
import { User, Plus, X, Loader2 } from 'lucide-react';
import { useCan } from '../../contexts/useAuth';

interface Driver {
  driver_uid: string;
  full_name: string;
}

interface ZoneDriversProps {
  zoneId: string;
  zoneName: string;
  assignedDriverUids: string[];
  availableDrivers: Driver[];
  onAddDriver: (driverUid: string) => Promise<void>;
  onRemoveDriver: (driverUid: string) => Promise<void>;
  isLoading?: boolean;
}

export default function ZoneDrivers({
  zoneName,
  assignedDriverUids,
  availableDrivers,
  onAddDriver,
  onRemoveDriver,
}: ZoneDriversProps) {
  const can = useCan();
  const [addingDriver, setAddingDriver] = useState<string | null>(null);
  const [removingDriver, setRemovingDriver] = useState<string | null>(null);

  const assignedDrivers = availableDrivers.filter((d) =>
    assignedDriverUids.includes(d.driver_uid)
  );

  const unassignedDrivers = availableDrivers.filter(
    (d) => !assignedDriverUids.includes(d.driver_uid)
  );

  const handleAddDriver = async (driverUid: string) => {
    setAddingDriver(driverUid);
    try {
      await onAddDriver(driverUid);
    } finally {
      setAddingDriver(null);
    }
  };

  const handleRemoveDriver = async (driverUid: string) => {
    setRemovingDriver(driverUid);
    try {
      await onRemoveDriver(driverUid);
    } finally {
      setRemovingDriver(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Assigned Drivers */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-gray-900">
            Assigned Drivers
          </p>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {assignedDrivers.length}
          </span>
        </div>
        {assignedDrivers.length === 0 ? (
          <p className="text-sm text-gray-500 py-3 text-center bg-gray-50 rounded-lg">
            No drivers assigned to {zoneName}
          </p>
        ) : (
          <div className="space-y-2">
            {assignedDrivers.map((driver) => (
              <div
                key={driver.driver_uid}
                className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {driver.full_name}
                  </p>
                </div>
                {can('zones.manage') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveDriver(driver.driver_uid)}
                    disabled={removingDriver === driver.driver_uid}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    {removingDriver === driver.driver_uid ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Drivers */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-gray-900">
            Available Drivers
          </p>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {unassignedDrivers.length}
          </span>
        </div>
        {unassignedDrivers.length === 0 ? (
          <p className="text-sm text-gray-500 py-3 text-center bg-gray-50 rounded-lg">
            All drivers are assigned
          </p>
        ) : (
          <div className="space-y-2 overflow-y-auto flex-1 pr-1">
            {unassignedDrivers.map((driver) => (
              <div
                key={driver.driver_uid}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-full">
                    <User className="h-4 w-4 text-gray-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {driver.full_name}
                  </p>
                </div>
                {can('zones.manage') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddDriver(driver.driver_uid)}
                    disabled={addingDriver === driver.driver_uid}
                    leftIcon={
                      addingDriver === driver.driver_uid ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Plus className="h-3.5 w-3.5" />
                      )
                    }
                  >
                    Add
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
